import { Box } from "@mantine/core"

import inkSrc from "../assets/civala-logo-ink.png"
import whiteSrc from "../assets/civala-logo-white.png"

// The Civala wordmark is a bespoke high-contrast serif reproduced ONLY via the
// supplied logo asset — never retyped as live text in a serif font (per the
// civala-branding spec). Two color variants ship: ink for light grounds, white
// for dark or gold ones. Native ratio is 1338x392 (~3.41:1); scale by height
// and let width follow, never stretch or recolor.

const NATIVE_RATIO = 1338 / 392

type Variant = "ink" | "white"

type Props = {
  /** Rendered height in px. Width is derived from the native aspect ratio. */
  readonly height?: number
  /**
   * Which color variant to draw. `auto` renders both and lets the Mantine
   * color scheme pick — for chrome that flips between light and dark grounds.
   */
  readonly variant?: Variant | "auto"
}

type MarkProps = {
  readonly src: string
  readonly height: number
  /** Mantine color-scheme visibility. Exactly one is set by the `auto` variant. */
  readonly lightHidden?: boolean
  readonly darkHidden?: boolean
}

function Mark({ src, height, lightHidden, darkHidden }: MarkProps) {
  const width = Math.round(height * NATIVE_RATIO)
  return (
    <Box
      component="img"
      src={src}
      alt="Civala"
      // Intrinsic width/height as well as the layout ones, so the row does not
      // reflow between first paint and image decode.
      width={width}
      height={height}
      w={width}
      h={height}
      lightHidden={lightHidden}
      darkHidden={darkHidden}
      // No `display` here: the variant="auto" pair is hidden per color scheme by
      // Mantine's light/dark-hidden classes, and an inline display would outrank
      // them. Every consumer places the mark in a flex row, which blockifies it.
      style={{ flexShrink: 0, objectFit: "contain" }}
    />
  )
}

export function CivalaWordmark({ height = 22, variant = "ink" }: Props) {
  if (variant === "auto") {
    // Both are in the DOM; Mantine's hidden props swap them per color scheme.
    // A single <img> with a scheme-driven src would flash on scheme change.
    return (
      <>
        <Mark src={inkSrc} height={height} darkHidden />
        <Mark src={whiteSrc} height={height} lightHidden />
      </>
    )
  }
  return <Mark src={variant === "white" ? whiteSrc : inkSrc} height={height} />
}
